import { Link } from "react-router-dom";
import { ArrowRight, FileText, Building, Users, Briefcase, Calculator, Scale, PiggyBank, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { FadeIn, StaggerContainer, StaggerItem } from "@/components/FadeIn";
import { SectionHeading, GoldDivider, GoldDividerLeft } from "@/components/SectionHeading";

const services = [
    {
        icon: FileText,
        title: "Einkommensteuererklärung",
        description: "Umfassende Bearbeitung Ihrer Einkommensteuererklärung unter Berücksichtigung aller relevanten Einkünfte und Abzugsmöglichkeiten.",
        features: ["Arbeitnehmereinkünfte", "Vermietung & Verpachtung", "Kapitalerträge", "Freiberufliche Tätigkeit"],
    },
    {
        icon: Building,
        title: "Unternehmensberatung",
        description: "Steuerliche Beratung für Unternehmen aller Rechtsformen von der Gründung bis zur Nachfolgeplanung.",
        features: ["Rechtsformwahl", "Steueroptimierung", "Jahresabschlüsse", "Betriebsprüfungsbegleitung"],
    },
    {
        icon: Users,
        title: "Lohnbuchhaltung",
        description: "Professionelle Abwicklung Ihrer Lohn- und Gehaltsabrechnungen mit allen gesetzlichen Meldungen.",
        features: ["Lohnabrechnung", "Sozialversicherung", "Lohnsteueranmeldung", "Bescheinigungen"],
    },
    {
        icon: PiggyBank,
        title: "Erbschaft & Schenkung",
        description: "Beratung bei Vermögensübertragungen mit optimaler Nutzung steuerlicher Freibeträge.",
        features: ["Erbschaftsteuer", "Schenkungsteuer", "Nachfolgeplanung", "Freibetragsoptimierung"],
    },
    {
        icon: Calculator,
        title: "Finanzbuchhaltung",
        description: "Laufende Buchführung und Erstellung aussagekräftiger betriebswirtschaftlicher Auswertungen.",
        features: ["Laufende Buchführung", "BWA-Erstellung", "Mahnwesen", "Zahlungsverkehr"],
    },
    {
        icon: TrendingUp,
        title: "Existenzgründung",
        description: "Umfassende Begleitung bei der Gründung Ihres Unternehmens von der Planung bis zur Umsetzung.",
        features: ["Businessplanung", "Rechtsformberatung", "Finanzierungsberatung", "Fördermittel"],
    },
];

const targetGroups = [
    {
        title: "Privatpersonen",
        description: "Einkommensteuererklärungen, Arbeitnehmerveranlagung, Kapitalvermögen",
    },
    {
        title: "Freiberufler",
        description: "Künstler, Journalisten, Influencer, Berater und alle freien Berufe",
    },
    {
        title: "Unternehmer",
        description: "Einzelunternehmen, Personengesellschaften, Kapitalgesellschaften",
    },
    {
        title: "Family Offices",
        description: "Vermögensverwaltung, Nachfolgeplanung, internationale Strukturen",
    },
];

export default function Services() {
    return (
        <div className="bg-background">
            {/* Hero Section */}
            <section className="relative pt-32 lg:pt-40 pb-20 lg:pb-24 overflow-hidden">
                {/* Background Image */}
                <div className="absolute inset-0">
                    <img
                        src="https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=1920&q=80&auto=format&fit=crop"
                        alt="Steuerberatung Leistungen"
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-background/90 via-background/70 to-background/40" />
                    <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-background/30 to-background/60" />
                </div>
                
                <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
                    <div className="max-w-4xl">
                        <FadeIn>
                            <span className="font-sans text-xs tracking-wide-premium uppercase text-gold mb-6 block">
                                Leistungen
                            </span>
                        </FadeIn>

                        <FadeIn delay={0.1}>
                            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-foreground leading-tight mb-8">
                                Umfassende steuerliche Beratung
                            </h1>
                        </FadeIn>

                        <FadeIn delay={0.2}>
                            <GoldDividerLeft className="mb-8" />
                        </FadeIn>

                        <FadeIn delay={0.3}>
                            <p className="font-sans text-base md:text-lg text-muted-foreground leading-relaxed max-w-2xl">
                                Von der klassischen Steuererklärung bis zur komplexen Unternehmensberatung – 
                                wir bieten Ihnen das gesamte Spektrum steuerlicher Dienstleistungen aus einer Hand.
                            </p>
                        </FadeIn>
                    </div>
                </div>
            </section>

            {/* Services Grid */}
            <section className="py-20 lg:py-24 bg-charcoal-mid">
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <StaggerContainer className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" staggerDelay={0.1}>
                        {services.map((service) => (
                            <StaggerItem key={service.title}>
                                <Card className="bg-card border-border p-8 h-full hover:border-gold/30 transition-colors duration-400 group">
                                    <div className="flex flex-col h-full">
                                        <service.icon className="h-6 w-6 text-gold mb-6" strokeWidth={1.5} />
                                        
                                        <h3 className="font-serif text-xl text-foreground mb-4 group-hover:text-gold transition-colors duration-300">
                                            {service.title}
                                        </h3>
                                        
                                        <p className="font-sans text-sm text-muted-foreground leading-relaxed mb-6">
                                            {service.description}
                                        </p>
                                        
                                        <div className="mt-auto pt-6 border-t border-border">
                                            <ul className="space-y-2">
                                                {service.features.map((feature) => (
                                                    <li key={feature} className="flex items-center gap-2">
                                                        <div className="w-1 h-1 bg-gold rounded-full" />
                                                        <span className="font-sans text-xs text-muted-foreground">
                                                            {feature}
                                                        </span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>
                                </Card>
                            </StaggerItem>
                        ))}
                    </StaggerContainer>
                </div>
            </section>

            {/* Additional Services */}
            <section className="py-24 lg:py-32 bg-background">
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="grid lg:grid-cols-2 gap-16 lg:gap-24">
                        <div>
                            <FadeIn>
                                <SectionHeading
                                    eyebrow="Weitere Leistungen"
                                    title="Vertretung vor Behörden und Gerichten"
                                />
                            </FadeIn>

                            <FadeIn delay={0.1}>
                                <div className="mt-8 space-y-6 font-sans text-muted-foreground leading-relaxed">
                                    <p>
                                        Selbstverständlich vertritt unsere Kanzlei auch Ihre Interessen 
                                        in allen Steuerangelegenheiten gegenüber sämtlichen zuständigen 
                                        Behörden und Gerichten.
                                    </p>
                                    <p>
                                        Wir begleiten Sie bei:
                                    </p>
                                    <ul className="space-y-3">
                                        <li className="flex items-start gap-3">
                                            <div className="w-1.5 h-1.5 bg-gold rounded-full mt-2 flex-shrink-0" />
                                            <span>Betriebsprüfungen und Außenprüfungen</span>
                                        </li>
                                        <li className="flex items-start gap-3">
                                            <div className="w-1.5 h-1.5 bg-gold rounded-full mt-2 flex-shrink-0" />
                                            <span>Einspruchsverfahren gegen Steuerbescheide</span>
                                        </li>
                                        <li className="flex items-start gap-3">
                                            <div className="w-1.5 h-1.5 bg-gold rounded-full mt-2 flex-shrink-0" />
                                            <span>Klageverfahren vor dem Finanzgericht</span>
                                        </li>
                                        <li className="flex items-start gap-3">
                                            <div className="w-1.5 h-1.5 bg-gold rounded-full mt-2 flex-shrink-0" />
                                            <span>Verbindliche Auskünfte beim Finanzamt</span>
                                        </li>
                                    </ul>
                                </div>
                            </FadeIn>
                        </div>

                        <div>
                            <FadeIn delay={0.2}>
                                <Card className="bg-card border-border p-10">
                                    <h3 className="font-serif text-2xl text-foreground mb-8">
                                        Zielgruppen
                                    </h3>
                                    
                                    <div className="space-y-6">
                                        {targetGroups.map((group, index) => (
                                            <div key={group.title}>
                                                <div className="flex items-center gap-4 mb-2">
                                                    <span className="font-sans text-xs text-gold">
                                                        {String(index + 1).padStart(2, '0')}
                                                    </span>
                                                    <h4 className="font-serif text-lg text-foreground">
                                                        {group.title}
                                                    </h4>
                                                </div>
                                                <p className="font-sans text-sm text-muted-foreground pl-8">
                                                    {group.description}
                                                </p>
                                                {index < targetGroups.length - 1 && (
                                                    <Separator className="bg-border mt-6" />
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </Card>
                            </FadeIn>
                        </div>
                    </div>
                </div>
            </section>

            {/* FAQ Teaser */}
            <section className="relative py-24 lg:py-32 overflow-hidden">
                {/* Background Image */}
                <div className="absolute inset-0">
                    <img
                        src="https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1920&q=80&auto=format&fit=crop"
                        alt="Häufige Fragen"
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-charcoal-mid/90" />
                </div>
                
                <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
                    <FadeIn>
                        <SectionHeading
                            eyebrow="Häufige Fragen"
                            title="Fragen, die Sie beschäftigen"
                            alignment="center"
                            className="mb-16"
                        />
                    </FadeIn>

                    <StaggerContainer className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto" staggerDelay={0.1}>
                        {[
                            "Was gibt es zu beachten, wenn ich einen sogenannten Minijobber einstelle?",
                            "Können Sie uns auch in der Erbschaftssteuer beraten?",
                            "Welche Rechtsformen betreuen Sie?",
                            "Können Sie unsere Lohnbuchhaltung übernehmen?",
                        ].map((question, index) => (
                            <StaggerItem key={index}>
                                <Card className="bg-card border-border p-6 hover:border-gold/30 transition-colors duration-400">
                                    <p className="font-serif text-lg text-foreground">
                                        {question}
                                    </p>
                                </Card>
                            </StaggerItem>
                        ))}
                    </StaggerContainer>

                    <FadeIn delay={0.4}>
                        <div className="text-center mt-12">
                            <Button
                                asChild
                                variant="ghost"
                                className="text-gold hover:text-foreground hover:bg-transparent transition-colors duration-300 font-sans text-sm tracking-premium uppercase"
                            >
                                <Link to="/kontakt">
                                    Stellen Sie Ihre Frage
                                    <ArrowRight className="ml-2 h-4 w-4" />
                                </Link>
                            </Button>
                        </div>
                    </FadeIn>
                </div>
            </section>

            {/* CTA */}
            <section className="py-24 lg:py-32 bg-background border-t border-border">
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="max-w-3xl mx-auto text-center">
                        <FadeIn>
                            <GoldDivider className="mb-10" />
                        </FadeIn>
                        
                        <FadeIn delay={0.1}>
                            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-foreground mb-6">
                                Lassen Sie uns über Ihre Anforderungen sprechen
                            </h2>
                        </FadeIn>

                        <FadeIn delay={0.2}>
                            <p className="font-sans text-base md:text-lg text-muted-foreground mb-10">
                                Jedes Mandat ist individuell. Wir nehmen uns Zeit, 
                                Ihre spezifischen Bedürfnisse zu verstehen.
                            </p>
                        </FadeIn>

                        <FadeIn delay={0.3}>
                            <Button
                                asChild
                                className="bg-gold text-primary-foreground hover:bg-gold-muted transition-all duration-400 px-10 py-6 font-sans text-sm tracking-premium uppercase"
                            >
                                <Link to="/kontakt">
                                    Beratungsgespräch vereinbaren
                                    <ArrowRight className="ml-2 h-4 w-4" />
                                </Link>
                            </Button>
                        </FadeIn>
                    </div>
                </div>
            </section>
        </div>
    );
}
